/**
 * Wallet Repository — READ side of the personal wallet.
 *
 * Firestore structure:
 * - users/{uid}/wallet/summary (balance, totals)
 * - users/{uid}/wallet/summary/earnings/{id}
 * - users/{uid}/wallet/summary/expenses/{id}
 * - users/{uid}/wallet/summary/usage/{id}
 * - accounts/{accountId}/wallet/displaySummary (server-written display mirror)
 *
 * SERVER-WRITE-ONLY (security remediation slice 2, 2026-09-15 —
 * docs/PLAN-security-remediation-20260915.md §2.1). Until this slice the
 * browser wrote its own balance from here: createExpenseWithBalanceUpdate
 * debited it after every model call, initializeNewUserWallet seeded the $100
 * welcome credit, and migrateLegacyWalletIfNeeded copied a legacy balance in.
 * That is the same access that let a signed-in user set any balance and cash
 * it out through Stripe. Now:
 *   - model-call debits are made by the LLM proxies' server meter
 *     (src/services/proxyMeter.ts → recordAccountExpense);
 *   - the welcome credit and the legacy migration run in the
 *     ensurePersonalWallet callable (functions/src/wallet/personalWallet.ts);
 *   - firestore.rules denies every client write under users/{uid}/wallet.
 * This module therefore imports NO Firestore write primitive — pinned by
 * src/services/__tests__/walletClientReadOnly.test.ts. The legacy
 * payment-methods doc (users/{uid}/user_wallet) lives in
 * ./userWalletPaymentMethods.ts.
 */

import { parsePlanAllocationView, type PlanAllocationView } from '../config/planAllocation';
import { parseBillingPauseView, type BillingPauseView } from '../config/billingPause';
import { onSnapshot, Unsubscribe } from 'firebase/firestore';
import { Paths, withBackoff, getDoc, doc, db } from './firestoreUtils';
import { resolveTabWorkspace } from '../services/prest0nRuntime/tabWorkspace';
import { pickBackPointerAccountId } from '../services/prest0nRuntime/backPointer';
import {
  WalletSummary,
  WalletSummaryDoc,
  fromWalletSummaryDoc,
  createDefaultWalletSummary,
} from '../types/walletFirestore';
import type { WelcomeCreditOutcome } from '../constants/walletVocabulary';

// ============================================================================
// Server-side wallet preparation (ensurePersonalWallet callable)
// ============================================================================

export interface EnsurePersonalWalletResult {
  /** True when a legacy wallet_olv_balance was copied into a NEW wallet/summary doc. */
  migrated: boolean;
  migratedFrom?: 'user_wallet_page' | 'user_wallet';
  /** The server's word for the welcome credit (the walletVocabulary twin). */
  welcomeCredit: WelcomeCreditOutcome;
}

async function callEnsurePersonalWallet(welcomeCredit: boolean): Promise<EnsurePersonalWalletResult> {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const fn = httpsCallable<{ welcomeCredit: boolean }, EnsurePersonalWalletResult>(
    getFunctions(undefined, 'us-central1'),
    'ensurePersonalWallet',
  );
  const result = await fn({ welcomeCredit });
  return result.data;
}

// Track which UIDs have already been checked to avoid repeated migration attempts
const _migrationAttempted = new Set<string>();

/**
 * One-time migration: when the new `wallet/summary` doc doesn't exist, ask the
 * server to seed it from the legacy wallet paths
 * (users/{uid}/user_wallet_page → users/{uid}/user_wallet, `wallet_olv_balance`).
 *
 * The server copies a NON-ZERO legacy balance only, inside a transaction that
 * refuses when the doc already exists — so a concurrent server deposit can
 * never be clobbered (the 2026-09-09 trial-credit-loss rule, kept verbatim on
 * the Admin SDK). The real-time listener picks the new doc up on its own.
 */
async function migrateLegacyWalletIfNeeded(uid: string): Promise<void> {
  if (_migrationAttempted.has(uid)) return;
  _migrationAttempted.add(uid);
  try {
    await callEnsurePersonalWallet(false);
  } catch (err) {
    console.error('[WalletRepository] Legacy wallet migration failed:', err);
    // Don't block — the listener will still return defaults
  }
}

/**
 * New-user wallet initialization. The $100 welcome credit it once seeded for code /
 * team-invite signups is retired (W-R2a, 2026-10-30: the free internship comes with a
 * new organization), so the server now only makes sure the wallet exists.
 * Runs server-side (ensurePersonalWallet with welcomeCredit:true) for the
 * SIGNED-IN uid; `uid` is the caller's expectation and only checked for
 * agreement. Idempotent: a wallet that already holds money, or already carries
 * the welcome-credit earning row, is left alone.
 */
export async function initializeNewUserWallet(uid: string): Promise<void> {
  const { getAuth } = await import('firebase/auth');
  const signedIn = getAuth().currentUser?.uid || null;
  if (signedIn && signedIn !== uid) {
    console.warn('[WalletRepository] initializeNewUserWallet: uid mismatch with the signed-in user; the server seeds the signed-in wallet only', {
      requested: uid,
      signedIn,
    });
  }
  const result = await callEnsurePersonalWallet(true);
  if (result.welcomeCredit === 'rate-unavailable') {
    console.warn('[WalletRepository] welcome credit not granted: pr0 rate unavailable');
  }
}

// ============================================================================
// Wallet Summary Operations
// ============================================================================

/**
 * Get the current wallet summary
 */
export async function getWalletSummary(uid: string): Promise<WalletSummary> {
  const ref = Paths.walletSummaryDoc(uid);
  const snap = await withBackoff(() => getDoc(ref));

  if (!snap.exists()) {
    // Attempt to migrate from legacy paths — after migration the caller
    // should re-read (or the real-time listener will pick up the new doc).
    await migrateLegacyWalletIfNeeded(uid);
    const retrySnap = await withBackoff(() => getDoc(ref));
    if (retrySnap.exists()) {
      return fromWalletSummaryDoc(retrySnap.data() as WalletSummaryDoc);
    }
    return createDefaultWalletSummary();
  }

  return fromWalletSummaryDoc(snap.data() as WalletSummaryDoc);
}

/**
 * Subscribe to wallet summary with real-time updates
 */
export function onWalletSummarySnapshot(
  uid: string,
  onData: (summary: WalletSummary) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const ref = Paths.walletSummaryDoc(uid);

  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        console.warn('[WalletRepository] wallet/summary NOT FOUND at:', ref.path);
        // Return defaults immediately so the UI isn't stuck loading
        onData(createDefaultWalletSummary());
        // Kick off migration in the background — once the new doc is written,
        // this same onSnapshot listener will fire again with real data.
        migrateLegacyWalletIfNeeded(uid);
        return;
      }
      const summary = fromWalletSummaryDoc(snap.data() as WalletSummaryDoc);
      onData(summary);
    },
    (error) => {
      console.error('[WalletRepository] Wallet summary snapshot error:', error);
      onError?.(error);
    }
  );
}

// ============================================================================
// Account wallet DISPLAY mirror (owner decision 2026-07-13: "display")
// ============================================================================

/**
 * Snapshot of accounts/{accountId}/wallet/displaySummary — the server-written
 * DISPLAY mirror of whichever wallet actually PAYS for the account's usage
 * (owner-personal or workspace pool; see functions/src/lib/accountExpense.ts).
 * Balance fields only; readable by verified account members (firestore.rules
 * accounts/{id}/wallet/** block); written exclusively by Cloud Functions.
 */
export interface AccountWalletDisplaySnapshot {
  /** Whether the mirror doc exists (false until the account's first debit). */
  exists: boolean;
  /** Converted balance summary (null when the doc doesn't exist). */
  summary: WalletSummary | null;
  /**
   * Whose PERSONAL wallet pays (owner-personal mode) or null for the shared
   * workspace pool. Lets the client prefer its own live wallet feed when the
   * signed-in user IS the payer.
   */
  payerUid: string | null;
  /** 'owner-personal' | 'workspace' as stamped by the server (null if absent). */
  walletMode: string | null;
  /** A paid plan's monthly allocation (2026-10-11), when the account has one; null otherwise. */
  planAllocation?: PlanAllocationView | null;
  /** A paid plan paused for a failed payment (2026-10-11, no grace); null when never paused. */
  billingPause?: BillingPauseView | null;
}

/**
 * Subscribe to the account wallet display mirror with real-time updates.
 * onError fires for permission errors (e.g. not a verified member) — callers
 * must treat that as "fall back to the personal wallet".
 */
export function onAccountWalletDisplaySnapshot(
  accountId: string,
  onData: (snapshot: AccountWalletDisplaySnapshot) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const ref = doc(db, 'accounts', accountId, 'wallet', 'displaySummary');
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onData({ exists: false, summary: null, payerUid: null, walletMode: null, planAllocation: null, billingPause: null });
        return;
      }
      const data = snap.data() as WalletSummaryDoc & {
        payer_uid?: string | null;
        wallet_mode?: string;
        plan_allocation?: unknown;
        billing_pause?: unknown;
      };
      onData({
        exists: true,
        summary: fromWalletSummaryDoc(data),
        payerUid: typeof data.payer_uid === 'string' && data.payer_uid ? data.payer_uid : null,
        walletMode: typeof data.wallet_mode === 'string' && data.wallet_mode ? data.wallet_mode : null,
        planAllocation: parsePlanAllocationView(data.plan_allocation),
        billingPause: parseBillingPauseView(data.billing_pause),
      });
    },
    (error) => {
      console.warn('[WalletRepository] account wallet display snapshot error:', error);
      onError?.(error);
    }
  );
}

/**
 * Subscribe to the acting-account back-pointer (users/{uid}/account_membership/main
 * → accountId) used to resolve WHICH account's paying wallet to display. Always
 * self-readable per firestore.rules; emits null when absent. Kept here (rather
 * than the prest0nRuntime resolver, which is one-shot/cached) because the wallet
 * display must react to membership changes in real time.
 */
export function onAccountMembershipAccountIdSnapshot(
  uid: string,
  onData: (accountId: string | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const ref = doc(db, 'users', uid, 'account_membership', 'main');
  return onSnapshot(
    ref,
    (snap) => {
      // Full back-pointer chain (org-stickiness round 2, 2026-08-19): the
      // narrow accountId-only read showed the PERSONAL wallet to members
      // whose pointer still had the legacy invite-accept shape.
      const data = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
      const pointer = pickBackPointerAccountId(data);
      // PER-TAB ORGANIZATION (owner report 2026-09-11). This listener is
      // realtime, and the document it watches is ACCOUNT-WIDE: when another
      // device switched, the balance, expenses and VM banner here flipped to
      // the other organization within milliseconds while the rest of this page
      // — chat, threads, the header pill — stayed put. That was the visible
      // half of "some other oddities": one page showing two organizations.
      // The tab's own workspace decides; the pointer is the fallback it always
      // was, so a tab with no pin behaves exactly as before.
      onData(resolveTabWorkspace({ uid, accountFromPointer: pointer }).accountId);
    },
    (error) => {
      console.warn('[WalletRepository] account membership snapshot error:', error);
      onError?.(error);
    }
  );
}
